import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, Subject, take, takeUntil } from 'rxjs';
import { MealRelation, MealRelationLabels } from 'src/app/common/enums/mealRelation-type';
import { generateSuggestions, formatDateOnlyForApi } from 'src/app/common/function-helper';
import { MaterialModule } from 'src/app/material.module';
import { CommonDatePipe } from 'src/app/pipe/common-date.pipe';
import { TimeFormatPipe } from 'src/app/pipe/time.pipe';
import { DrugService } from 'src/app/pages/settings/drug-tab/drug-list/drug.service';
import { IDrugDetail } from 'src/app/pages/settings/drug-tab/drug-list/idrug';
import { IPatientInfo } from 'src/app/pages/patient/ipatient-info';
import { Iopd } from 'src/app/pages/opd/opddetails/iopddetails';
import { Iipd } from 'src/app/pages/ipd/Iipd.type';
import { IUsers } from 'src/app/pages/user/iusers';
import { TablerIconsModule } from 'angular-tabler-icons';
import { environment } from 'src/environments/environment';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { DosePatternDirective } from 'src/app/common/directive/dosePattern.directive';
import { IPatientDailyReport, IPatientDailyReportMedicine, PatientDailyReportDialogResult, PatientDailyReportFormData, PatientDailyReportFormMode } from '../patient-daily-report.types';
import { PatientDailyReportService } from '../patient-daily-report.service';
import { TemplateListComponent } from 'src/app/pages/template/template-list/template-list.component';
import { TemplateType } from 'src/app/common/enums/template-type';
import { LookupService } from 'src/app/services/lookup.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { PrescriptionTemplateService } from 'src/app/pages/dialogs/prescription-template/prescription-template.service';
import { IPrescription, IPrescriptionItem, PrescriptionType } from 'src/app/pages/opd/prescription/iprescription';
import { ConfirmationDialog } from 'src/app/pages/dialogs/confirmation/confirmation-dialog.component';
import { PrescriptionTemplateComponent } from 'src/app/pages/dialogs/prescription-template/prescription-template.component';
import { VitalDetailsComponent } from 'src/app/pages/opd/vital-details/vital-details.component';
import { VitalDetailsValue } from 'src/app/pages/opd/vital-details/vital.type';


import { HasRoleDirective } from 'src/app/common/directive/has-role.directive';
import { ROLE_GROUPS } from 'src/app/common/authorization/roles';

@Component({
  selector: 'app-patient-daily-report',
  standalone: true,
  imports: [
    HasRoleDirective,
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
    DosePatternDirective,
    VitalDetailsComponent
  ],
   providers: [
    DatePipe, CommonDatePipe, TimeFormatPipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './patient-daily-report.component.html',
  styleUrl: './patient-daily-report.component.scss'
})
export class PatientDailyReportComponent implements OnInit, OnDestroy {
  readonly ROLE_GROUPS = ROLE_GROUPS;
  @ViewChild(MatAutocompleteTrigger) templateAutoTrigger!: MatAutocompleteTrigger;
  @ViewChild(VitalDetailsComponent) vitalDetailsComponent?: VitalDetailsComponent;
  @Input() patientDetails?: IPatientInfo;
  @Input() opdDetails?: Iopd | null;
  @Input() ipdData?: Iipd | null;
  @Input() doctorData?: IUsers | null;
  @Input() patientId?: number;
  @Input() opdId?: number | null;
  @Input() ipdId?: number | null;
  @Input() ipdNumber?: string | null;
  @Input() doctorId?: number;
  @Input() readOnly = false;
  @Input() isDialog = false;
  @Input() mode: PatientDailyReportFormMode = 'create';
  @Input() report?: IPatientDailyReport;

  selectedReport?: IPatientDailyReport;
  reportForm!: FormGroup;
  medicines!: FormArray<FormGroup>;
  listOfDrugs: IDrugDetail[] = [];
  rowFilteredDrugs$: Observable<IDrugDetail[]>[] = [];
  dosePatternSuggestions: string[][] = [];
  durationSuggestions: string[][] = [];
  mealRelationList = Object.values(MealRelation).filter(x => typeof x === 'number') as MealRelation[];
  mealRelationData = MealRelationLabels;
  TemplateType = TemplateType;
  templateCtrl = new FormControl('');
  filteredTemplates: CommonDropDown[] = [];
  isSaving = false;
  staticImagePath = environment.imagePath;
  currentVitals: VitalDetailsValue = {};
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private reportService: PatientDailyReportService,
    private drugService: DrugService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private commonDatePipe: CommonDatePipe,
    private timeFormatPipe: TimeFormatPipe,
    private dialog: MatDialog,
    private lookupService: LookupService,
    private prescriptionTemplateService: PrescriptionTemplateService,
    @Optional() private dialogRef: MatDialogRef<PatientDailyReportComponent, PatientDailyReportDialogResult>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: PatientDailyReportFormData
  ) {}

  ngOnInit(): void {
    this.applyDialogData();
    this.initializeForm();
    this.loadTemplateList();
    this.initializeTemplateSearch();
    this.loadDrugs();
    if (this.report) {
      this.selectReport(this.report);
    } else {
      this.startNewReport();
    }
    this.applyReadOnlyState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canEdit(): boolean {
    return !this.readOnly && this.mode !== 'view';
  }

  get contextTitle(): string {
    return this.ipdId ? 'IPD Day Report' : 'OPD Day Report';
  }

  get formTitleKey(): string {
    if (this.mode === 'view') {
      return 'LABEL.VIEW_LABEL';
    }
    return this.reportForm?.get('id')?.value ? 'LABEL.UPDATE_LABEL' : 'LABEL.ADD_LABEL';
  }

  get canAddTemplate(): boolean {
    return this.canEdit && this.getCompletedMedicineItems().length > 0;
  }

  private applyDialogData(): void {
    if (!this.data) return;
    this.patientDetails = this.patientDetails ?? this.data.patientDetails;
    this.opdDetails = this.opdDetails ?? this.data.opdDetails;
    this.ipdData = this.ipdData ?? this.data.ipdData;
    this.doctorData = this.doctorData ?? this.data.doctorData;
    this.patientId = this.patientId ?? this.data.patientId ?? this.data.patientDetails?.id;
    this.opdId = this.opdId ?? this.data.opdId ?? this.data.opdDetails?.id ?? null;
    this.ipdId = this.ipdId ?? this.data.ipdId ?? this.data.ipdData?.id ?? null;
    const ipdNumber = this.ipdNumber ?? this.data.ipdNumber ?? this.data.ipdData?.ipdNumber ?? null;
    this.ipdNumber = ipdNumber === null || ipdNumber === undefined ? null : String(ipdNumber);
    this.doctorId = this.doctorId ?? this.data.doctorId ?? this.data.doctorData?.id ?? this.data.opdDetails?.doctorId ?? this.data.ipdData?.doctorId;
    this.readOnly = this.readOnly || !!this.data.readOnly;
    this.isDialog = this.isDialog || !!this.data.isDialog;
    this.mode = this.data.mode ?? this.mode;
    this.report = this.report ?? this.data.report;
  }

  private initializeForm(): void {
    this.reportForm = this.fb.group({
      id: [0],
      reportDate: [new Date(), Validators.required],
      reportTime: [this.getCurrentTimeSpan()],
      dailynotes: [''],
      plan: ['']
    });

    this.medicines = this.fb.array<FormGroup>([]);
    this.reportForm.addControl('medicines', this.medicines);
    this.addMedicineRow();
  }

  private loadDrugs(): void {
    this.drugService.getDrugDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.listOfDrugs = response.result ?? [];
          this.refreshAllDrugFilters();
        }
      });
  }

  startNewReport(): void {
    this.selectedReport = undefined;
    this.currentVitals = this.getInitialVitals();
    this.reportForm.reset({
      id: 0,
      reportDate: new Date(),
      reportTime: this.getCurrentTimeSpan(),
      dailynotes: '',
      plan: ''
    });
    this.resetMedicineRows([]);
    this.applyReadOnlyState();
  }

  selectReport(report: IPatientDailyReport): void {
    this.selectedReport = report;
    this.currentVitals = this.getInitialVitals(report);
    this.reportForm.patchValue({
      id: report.id ?? 0,
      reportDate: report.reportDate ? new Date(report.reportDate) : new Date(),
      reportTime: this.normalizeTime(report.reportTime),
      dailynotes: report.dailynotes ?? '',
      plan: report.plan ?? '',
    });
    this.resetMedicineRows(report.medicines ?? []);
    this.applyReadOnlyState();
  }

  onSave(): void {
    if (!this.canEdit) 
      {
        return;
      }
    if (!this.getPatientId() || !this.getDoctorId()) {
      this.toastr.error(this.translate.instant('VALIDATION.FILL_REQUIRED_FIELDS'));
      return;
    }
    const medicinesValid = this.validateMedicineRows();
    if (this.vitalDetailsComponent?.invalid) {
      this.vitalDetailsComponent.markAllAsTouched();
    }
    if (this.reportForm.invalid || this.vitalDetailsComponent?.invalid || !medicinesValid) {
      this.reportForm.markAllAsTouched();
      this.toastr.error(this.translate.instant('VALIDATION.FILL_REQUIRED_FIELDS'));
      return;
    }

    const payload = this.buildPayload();
    if (!this.hasMeaningfulContent(payload)) {
      this.toastr.error(this.translate.instant('VALIDATION.ONE_DAILY_REPORT'));
      return;
    }

    this.isSaving = true;
    const request = payload.id && payload.id > 0
      ? this.reportService.update(payload)
      : this.reportService.create(payload);

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.isSaving = false;
        this.toastr.success(this.translate.instant(payload.id ? 'COMMON.UPDATE' : 'COMMON.SAVE').replace('{0}', this.translate.instant('ENTITY.DAY_REPORT')));
        this.selectedReport = response.result;
        this.dialogRef?.close({ saved: true, report: response.result });
      },
      error: err => {
        this.isSaving = false;
        const errors = err?.error?.errors ?? err?.errors;
        const message = errors?.length ? errors.join('\n') : this.translate.instant('COMMON.ERROR');
        this.toastr.error(message);
      }
    });
  }

  addMedicineRow(item?: IPatientDailyReportMedicine): void {
    if (!item && this.medicines?.length && this.isMedicineRowEmpty(this.medicines.at(this.medicines.length - 1))) {
      return;
    }

    const row = this.fb.group({
      id: [item?.id ?? 0],
      patientDailyReportId: [item?.patientDailyReportId ?? this.selectedReport?.id ?? 0],
      drugId: [item?.drugId ?? null],
      drugName: [item?.drugName ?? ''],
      dosePattern: [item?.dosePattern ?? ''],
      duration: [item?.duration ?? ''],
      mealRelation: [this.normalizeMealRelation(item?.mealRelation)],
      instruction: [item?.instruction ?? '']
    });

    this.medicines.push(row);
    const index = this.medicines.length - 1;
    this.setupDrugFilter(index);
    this.dosePatternSuggestions.push([]);
    this.durationSuggestions.push([]);
    row.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.validateMedicineRow(row));
  }

  onMedicineRowComplete(rowIndex: number): void {
    if (!this.canEdit) return;
    const row = this.medicines.at(rowIndex);
    if (this.isMedicineRowComplete(row) && this.isLastMedicineRow(rowIndex)) {
      this.addMedicineRow();
    }
  }

  removeMedicineRow(index: number): void {
    if (!this.canEdit) return;
    if (this.medicines.length === 1) {
      this.medicines.at(0).reset({ id: 0, patientDailyReportId: this.selectedReport?.id ?? 0 });
      return;
    }
    this.medicines.removeAt(index);
    this.rowFilteredDrugs$.splice(index, 1);
    this.dosePatternSuggestions.splice(index, 1);
    this.durationSuggestions.splice(index, 1);
  }

  onDrugSelected(event: MatAutocompleteSelectedEvent, rowIndex: number): void {
    const selected = event.option.value as IDrugDetail;
    this.medicines.at(rowIndex).patchValue({
      drugId: selected.id,
      drugName: selected.name
    });
    this.onMedicineRowComplete(rowIndex);
  }

  onDurationInput(event: Event, index: number): void {
    this.durationSuggestions[index] = generateSuggestions((event.target as HTMLInputElement).value);
  }

  displayDrug(drug: IDrugDetail | string | null): string {
    return typeof drug === 'string' ? drug : drug?.name ?? '';
  }

  getDrugName(drugId: number): string {
    const drug = this.listOfDrugs.find(item => item.id === drugId);
    return drug?.name ?? 'Unknown Drug';
  }

  displayTemplateName(template: CommonDropDown | string | null): string {
    return typeof template === 'string' ? template : template?.name ?? '';
  }

  onTemplateSelected(template: CommonDropDown): void {
    this.templateAutoTrigger?.closePanel();
    this.applyPrescriptionTemplate(template);
    this.templateCtrl.setValue('', { emitEvent: false });
  }

  onPrescriptionTemplate(): void {
    const prescriptionItems = this.getCompletedMedicineItems();
    if (!prescriptionItems.length) {
      return;
    }

    const prescriptionData: IPrescription = {
      id: this.reportForm.get('id')?.value ?? 0,
      prescriptionDate: new Date(),
      doctorId: this.getDoctorId(),
      patientId: this.getPatientId(),
      opdNumber: this.opdDetails?.opdNumber ?? '',
      opdId: this.opdId ?? this.opdDetails?.id ?? 0,
      ipdId: this.ipdId ?? undefined,
      ipdNumber: this.ipdNumber ?? undefined,
      patientDailyReportId: this.reportForm.get('id')?.value ?? undefined,
      prescriptionType: PrescriptionType.DailyReport,
      diagnosis: '',
      prescriptionItems
    };

    const dialogRef = this.dialog.open(PrescriptionTemplateComponent, {
      width: '520px',
      maxWidth: '90vw',
      data: {
        prescriptionItems: prescriptionData,
        isTemplateApplied: false
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.loadTemplateList();
          this.templateCtrl.setValue('', { emitEvent: false });
        }
      });
  }

  getReportDateDisplay(): string {
    const value = this.reportForm?.get('reportDate')?.value ?? this.selectedReport?.reportDate;
    return this.commonDatePipe.transform(value) || '-';
  }

  getReportTimeDisplay(): string {
    const value = this.reportForm?.get('reportTime')?.value ?? this.selectedReport?.reportTime;
    return value ? this.timeFormatPipe.transform(this.toApiTimeSpan(value) ?? value) : '-';
  }

  getDoctorNameDisplay(): string {
    if (this.selectedReport?.doctorName) {
      return this.selectedReport.doctorName;
    }
    const firstName = this.doctorData?.firstName ?? '';
    const lastName = this.doctorData?.lastName ?? '';
    const doctorName = `${firstName} ${lastName}`.trim();
    return doctorName ? `Dr. ${doctorName}` : '-';
  }

  closeDialog(): void {
    this.dialogRef?.close({ saved: false });
  }

  onVitalsChanged(vitals: VitalDetailsValue): void {
    this.currentVitals = vitals;
  }

  openTemplatePopup(templateType: TemplateType): void {
    if (!this.canEdit) {
      return;
    }

    const formFieldName = this.getTemplateFormField(templateType);
    if (!formFieldName) {
      return;
    }

    const templateDialog = this.dialog.open(TemplateListComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        templateType: templateType,
      }
    });

    templateDialog.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result?.selected && result.template) {
        this.reportForm.get(formFieldName)?.setValue(result.template.templateDescription || '');
      }
    });
  }

  private resetMedicineRows(items: IPatientDailyReportMedicine[]): void {
    this.medicines.clear();
    this.rowFilteredDrugs$ = [];
    this.dosePatternSuggestions = [];
    this.durationSuggestions = [];

    if (items.length) {
      items.forEach(item => this.addMedicineRow(item));
    }
    if (this.canEdit) {
      this.addMedicineRow();
    }
  }

  private setupDrugFilter(index: number): void {
    const row = this.medicines.at(index);
    this.rowFilteredDrugs$[index] = row.get('drugName')!.valueChanges.pipe(
      startWith(row.get('drugName')?.value ?? ''),
      debounceTime(200),
      map(value => this.filterDrugs(value))
    );
  }

  private filterDrugs(value: string | IDrugDetail | null): IDrugDetail[] {
    const searchText = typeof value === 'string' ? value : value?.name ?? '';
    const filterValue = searchText.toLowerCase();
    return this.listOfDrugs.filter(d => d.name.toLowerCase().includes(filterValue));
  }

  private refreshAllDrugFilters(): void {
    this.medicines?.controls.forEach(row => {
      const control = row.get('drugName');
      control?.setValue(control.value);
    });
  }

  private initializeTemplateSearch(): void {
    this.templateCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        if (!value) {
          this.loadTemplateList();
          return;
        }

        if (typeof value === 'string' && value.length >= 2) {
          this.loadTemplateList(value);
        }
      });
  }

  private loadTemplateList(keyword?: string): void {
    this.lookupService.getPrescriptionTemplateName({
      doctorId: this.getInitialDoctorId(),
      keyword: keyword || undefined
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.filteredTemplates = res?.result ?? [];
      });
  }

  private applyPrescriptionTemplate(template: CommonDropDown): void {
    if (!this.canEdit || !template?.id) {
      return;
    }

    this.prescriptionTemplateService.getPrescriptionTemplateById(template.id)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(response => {
        const templateMedicines = response?.result?.prescriptionTemplateDetails ?? [];
        const currentMedicines = this.medicines.controls
          .map(row => this.formGroupToMedicine(row))
          .filter(item => this.hasMedicineData(item));

        const normalizedTemplateMedicines: IPatientDailyReportMedicine[] = templateMedicines.map((item: IPrescriptionItem) => ({
          id: 0,
          patientDailyReportId: this.selectedReport?.id ?? 0,
          drugId: item.drugId,
          drugName: item.drugName || this.getDrugName(item.drugId),
          dosePattern: item.dosePattern,
          duration: item.duration,
          route: item.route,
          mealRelation: item.mealRelation,
          instruction: item.instruction
        }));

        const mergedMedicines = this.getUniqueMedicines([
          ...currentMedicines,
          ...normalizedTemplateMedicines
        ]);

        this.resetMedicineRows(mergedMedicines);
      });
  }

  private getUniqueMedicines(items: IPatientDailyReportMedicine[]): IPatientDailyReportMedicine[] {
    const map = new Map<number, IPatientDailyReportMedicine>();
    const medicinesWithoutDrugId: IPatientDailyReportMedicine[] = [];
    const duplicateDrugNames: string[] = [];

    items.forEach(item => {
      if (!item.drugId) {
        medicinesWithoutDrugId.push(item);
        return;
      }

      if (map.has(item.drugId)) {
        duplicateDrugNames.push(item.drugName || this.getDrugName(item.drugId));
        return;
      }

      map.set(item.drugId, item);
    });

    if (duplicateDrugNames.length > 0) {
      const uniqueNames = [...new Set(duplicateDrugNames)].join(', ');

      this.dialog.open(ConfirmationDialog, {
        width: '450px',
        data: {
          title: this.translate.instant('ENTITY.SKIP_DRUG'),
          message: `${this.translate.instant('LABEL.SKIP_DRUG_MESSAGE')} <strong>${uniqueNames}</strong>`
        }
      });
    }

    return [
      ...medicinesWithoutDrugId,
      ...Array.from(map.values())
    ];
  }

  isLastMedicineRow(index: number): boolean {
    return index === this.medicines.length - 1;
  }

  private isMedicineRowComplete(row: AbstractControl): boolean {
    return !!row.get('drugId')?.value
      && !!row.get('dosePattern')?.value?.trim()
      && !!row.get('duration')?.value?.trim();
  }

  private isMedicineRowEmpty(row: AbstractControl): boolean {
    return !row.get('drugId')?.value
      && !row.get('drugName')?.value
      && !row.get('dosePattern')?.value
      && !row.get('duration')?.value
      && !row.get('mealRelation')?.value
      && !row.get('instruction')?.value;
  }

  private applyReadOnlyState(): void {
    if (!this.reportForm) return;
    if (this.canEdit) {
      this.reportForm.enable({ emitEvent: false });
    } else {
      this.reportForm.disable({ emitEvent: false });
    }
  }

  private getTemplateFormField(templateType: TemplateType): 'dailynotes' | 'plan' | null {
    switch (templateType) {
      case TemplateType.DailyNotes:
        return 'dailynotes';
      case TemplateType.PlanAdvice:
        return 'plan';
      default:
        return null;
    }
  }

  private buildPayload(): IPatientDailyReport {
    const values = this.reportForm.value;
    const isNewReport = !(values.id && values.id > 0);
    const reportDate = isNewReport ? new Date() : values.reportDate;
    const reportTime = isNewReport ? this.getCurrentTimeSpan() : values.reportTime;
    const medicines = this.medicines.controls
      .map(row => this.formGroupToMedicine(row))
      .filter(item => this.hasMedicineData(item));

    return {
      id: values.id ?? 0,
      patientId: this.getPatientId(),
      doctorId: this.getDoctorId(),
      opdId: this.opdId ?? this.opdDetails?.id ?? null,
      ipdId: this.ipdId ?? this.ipdData?.id ?? null,
      reportDate: formatDateOnlyForApi(reportDate) ?? reportDate,
      reportTime: this.toApiTimeSpan(reportTime),
      plan: values.plan ?? '',
      dailynotes: values.dailynotes ?? '',
      bloodPressure: this.currentVitals.bloodPressure?.trim() || null,
      weight: this.toNullableNumber(this.currentVitals.weight),
      heartRate: this.toNullableNumber(this.currentVitals.heartRate),
      oxygenSaturation: this.toNullableNumber(this.currentVitals.oxygenSaturation),
      bodyTemperature: this.toNullableNumber(this.currentVitals.bodyTemperature),
      medicines
    };
  }

  private formGroupToMedicine(row: AbstractControl): IPatientDailyReportMedicine {
    const drugNameValue = row.get('drugName')?.value as string | IDrugDetail | null;
    return {
      id: row.get('id')?.value ?? 0,
      patientDailyReportId: row.get('patientDailyReportId')?.value ?? this.selectedReport?.id ?? 0,
      drugId: row.get('drugId')?.value,
      drugName: typeof drugNameValue === 'string' ? drugNameValue : drugNameValue?.name ?? '',
      dosePattern: row.get('dosePattern')?.value ?? '',
      duration: row.get('duration')?.value ?? '',
      mealRelation: row.get('mealRelation')?.value ?? null,
      instruction: row.get('instruction')?.value ?? ''
    };
  }

  private getCompletedMedicineItems(): IPrescriptionItem[] {
    return this.medicines.controls
      .filter(row => this.isMedicineRowComplete(row))
      .map(row => {
        const medicine = this.formGroupToMedicine(row);
        return {
          drugId: medicine.drugId ?? 0,
          drugName: medicine.drugName ?? this.getDrugName(medicine.drugId ?? 0),
          dosePattern: medicine.dosePattern ?? '',
          duration: medicine.duration ?? '',
          route: medicine.route,
          mealRelation: typeof medicine.mealRelation === 'string'
            ? this.normalizeMealRelation(medicine.mealRelation) ?? undefined
            : medicine.mealRelation ?? undefined,
          instruction: medicine.instruction
        };
      });
  }

  private hasMeaningfulContent(report: IPatientDailyReport): boolean {
    return [
      report.dailynotes,
      report.plan,
    ].some(value => !!value?.trim())
      || this.hasVitalData(report)
      || report.medicines.some(item => !!item.drugId && !!item.dosePattern?.trim() && !!item.duration?.trim());
  }

  private validateMedicineRows(): boolean {
    return this.medicines.controls
      .map(row => this.validateMedicineRow(row))
      .every(Boolean);
  }

  private validateMedicineRow(row: FormGroup): boolean {
    const medicine = this.formGroupToMedicine(row);
    const hasData = this.hasMedicineData(medicine);
    const drugControl = row.get('drugName');
    const dosePatternControl = row.get('dosePattern');
    const durationControl = row.get('duration');

    this.setControlError(drugControl, 'requiredMedicineDrug', hasData && !medicine.drugId);
    this.setControlError(dosePatternControl, 'requiredMedicineDosePattern', hasData && !medicine.dosePattern?.trim());
    this.setControlError(durationControl, 'requiredMedicineDuration', hasData && !medicine.duration?.trim());

    return !drugControl?.hasError('requiredMedicineDrug')
      && !dosePatternControl?.hasError('requiredMedicineDosePattern')
      && !durationControl?.hasError('requiredMedicineDuration');
  }

  private setControlError(control: AbstractControl | null, key: string, hasError: boolean): void {
    if (!control) {
      return;
    }

    const errors = { ...(control.errors ?? {}) };
    if (hasError) {
      errors[key] = true;
    } else {
      delete errors[key];
    }
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  private hasVitalData(report: Pick<IPatientDailyReport, 'bloodPressure' | 'weight' | 'heartRate' | 'oxygenSaturation' | 'bodyTemperature'>): boolean {
    return !!report.bloodPressure?.trim()
      || report.weight !== null && report.weight !== undefined && report.weight !== ''
      || report.heartRate !== null && report.heartRate !== undefined && report.heartRate !== ''
      || report.oxygenSaturation !== null && report.oxygenSaturation !== undefined && report.oxygenSaturation !== ''
      || report.bodyTemperature !== null && report.bodyTemperature !== undefined && report.bodyTemperature !== '';
  }

  private hasMedicineData(item: IPatientDailyReportMedicine): boolean {
    return !!item.drugId
      || !!item.drugName?.trim()
      || !!item.dosePattern?.trim()
      || !!item.duration?.trim()
      || (item.mealRelation !== null && item.mealRelation !== undefined && item.mealRelation !== '')
      || !!item.instruction?.trim();
  }

  private getPatientId(): number {
    return Number(this.patientId ?? this.patientDetails?.id ?? this.opdDetails?.patientId ?? 0);
  }

  private getDoctorId(): number {
    return Number(this.getInitialDoctorId() || this.selectedReport?.doctorId || 0);
  }

  private getInitialVitals(report?: IPatientDailyReport): VitalDetailsValue {
    if (report && this.hasVitalData(report)) {
      return {
        bloodPressure: report.bloodPressure ?? '',
        weight: report.weight ?? '',
        heartRate: report.heartRate ?? '',
        oxygenSaturation: report.oxygenSaturation ?? '',
        bodyTemperature: report.bodyTemperature ?? ''
      };
    }

    return {
      bloodPressure: '',
      weight: '',
      heartRate: '',
      oxygenSaturation: '',
      bodyTemperature: ''
    };
  }

  private toNullableNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private getInitialDoctorId(): number {
    return Number(this.ipdData?.doctorId ?? this.doctorId ?? this.doctorData?.id ?? this.opdDetails?.doctorId ?? 0);
  }

  private normalizeTime(value?: string | null): string {
    if (!value) return '';
    const parts = this.getTimeParts(value);
    if (!parts) return '';
    return `${parts.hours}:${parts.minutes}`;
  }

  private toApiTimeSpan(value?: string | null): string | null {
    const parts = this.getTimeParts(value);
    if (!parts) return null;
    return `${parts.hours}:${parts.minutes}:${parts.seconds}`;
  }

  private getTimeParts(value?: string | null): { hours: string; minutes: string; seconds: string } | null {
    if (!value) return null;
    const time = value.toString().trim();
    if (!time) return null;
    const parts = time.split(':');
    if (parts.length < 2 || parts.length > 3) return null;
    const [hours, minutes, seconds = '00'] = parts;
    if (!hours || !minutes) return null;
    return {
      hours: hours.padStart(2, '0'),
      minutes: minutes.padStart(2, '0'),
      seconds: seconds.padStart(2, '0')
    };
  }

  private getCurrentTimeSpan(): string {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  private normalizeMealRelation(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string') {
      return MealRelation[value as keyof typeof MealRelation] ?? Number(value) ?? null;
    }
    return value;
  }


}
